import React, { useEffect, useState } from 'react';
import { referralAPI } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Copy, Users, TrendingUp, Gift, Share2, Link2, Check, DollarSign } from 'lucide-react';

const Stat = ({ icon: Icon, label, value, color = 'text-white' }) => (
  <div className="p-5 rounded-xl border border-[#1a2823] bg-[#0a1210]">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
      <Icon size={12} className="text-[#00e676]" /> {label}
    </div>
    <div className={`mt-2 text-2xl font-extrabold mono ${color}`}>{value}</div>
  </div>
);

const Referral = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try { setData(await referralAPI.me()); }
    catch (e) { toast({ title: 'Failed to load referrals', description: e?.response?.data?.detail || 'Try again' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast({ title: 'Copied to clipboard', description: text.length > 40 ? text.slice(0, 40) + '…' : text });
    setTimeout(() => setCopiedField(null), 1600);
  };

  const shareUrl = data ? `${window.location.origin}/login?ref=${data.code}` : '';

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Trade on NEXUS FX PRO',
          text: 'Join me on NEXUS FX PRO — the institutional-grade multi-asset trading terminal.',
          url: shareUrl
        });
      } catch {}
    } else {
      copy(shareUrl, 'share');
    }
  };

  if (loading || !data) {
    return <div className="text-[#00e676] text-sm">Loading your referral hub…</div>;
  }

  return (
    <div className="space-y-6" data-testid="referral-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Gift size={22} className="text-[#00e676]" /> Referral Program
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Invite traders and earn <span className="text-[#00e676] font-semibold">{Math.round(data.commission_rate * 100)}%</span> lifetime commission on every deposit they make.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Referred Traders" value={data.referred_count} />
        <Stat icon={DollarSign} label="Total Commission" value={`$${data.total_commission.toFixed(2)}`} color="text-[#00e676]" />
        <Stat icon={TrendingUp} label="Commission Rate" value={`${Math.round(data.commission_rate * 100)}%`} />
        <Stat icon={Gift} label="Payouts" value={data.recent_commissions.length} />
      </div>

      {/* Share panel */}
      <div className="p-6 rounded-xl border border-[#00e676]/30 bg-gradient-to-br from-[#0a1a13] via-[#0a1210] to-[#0a1210] relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#00e676]/10 blur-3xl pointer-events-none" />
        <div className="relative grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] text-[#00e676] uppercase tracking-widest font-semibold mb-2">Your Code</div>
            <div className="flex items-center gap-2">
              <code className="text-3xl font-extrabold mono text-white tracking-widest bg-[#050807] border border-[#1a2823] rounded-lg px-4 py-2" data-testid="referral-code">
                {data.code}
              </code>
              <button
                data-testid="copy-referral-code-btn"
                onClick={() => copy(data.code, 'code')}
                className="btn-outline-green px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center gap-1"
              >
                {copiedField === 'code' ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
          </div>
          <div>
            <div className="text-[10px] text-[#00e676] uppercase tracking-widest font-semibold mb-2">Share Link</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 min-w-0">
                <Link2 size={12} className="text-gray-500 shrink-0" />
                <span className="text-xs text-white mono truncate" data-testid="referral-share-link">{shareUrl}</span>
              </div>
              <button
                data-testid="copy-referral-link-btn"
                onClick={() => copy(shareUrl, 'url')}
                className="btn-outline-green px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center gap-1"
              >
                {copiedField === 'url' ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
              <button
                data-testid="share-referral-btn"
                onClick={share}
                className="btn-green px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center gap-1"
              >
                <Share2 size={12} /> Share
              </button>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              You earn <span className="text-[#00e676] font-semibold">{Math.round(data.commission_rate * 100)}%</span> instantly on every deposit made by anyone who signs up with your link.
            </p>
          </div>
        </div>
      </div>

      {/* Downline */}
      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#141f1b] flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Your Downline ({data.downline.length})</h3>
          <span className="text-[10px] text-gray-500 uppercase tracking-wider">Live earnings</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Trader</th>
                <th>Email</th>
                <th>Joined</th>
                <th>Deposit Volume</th>
                <th className="text-right pr-4">Your Commission</th>
              </tr>
            </thead>
            <tbody>
              {data.downline.length === 0 && (
                <tr><td colSpan="5" className="py-14 text-center text-sm text-gray-500" data-testid="referral-empty-state">
                  No referrals yet. Share your link to start earning commission on every deposit.
                </td></tr>
              )}
              {data.downline.map(d => (
                <tr key={d.referred_user_id} className="border-t border-[#141f1b] hover:bg-[#0f1a17]">
                  <td className="px-4 py-3 text-white font-semibold">{d.name}</td>
                  <td className="text-gray-400 text-xs">{d.email}</td>
                  <td className="text-gray-500 text-xs mono">{d.joined_at?.slice(0, 10)}</td>
                  <td className="mono text-gray-300">${d.deposit_volume.toFixed(2)}</td>
                  <td className="mono font-semibold text-[#00e676] text-right pr-4">+${d.commission_earned.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent commissions */}
      {data.recent_commissions.length > 0 && (
        <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#141f1b]">
            <h3 className="text-sm font-bold text-white">Recent Payouts</h3>
          </div>
          <div className="divide-y divide-[#141f1b]">
            {data.recent_commissions.slice(0, 10).map((c, i) => (
              <div key={c.transaction_id || c.created_at || i} className="px-4 py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="text-white text-xs mono">Deposit of ${c.source_amount?.toFixed(2)}</div>
                  <div className="text-[10px] text-gray-500 mono">{c.created_at?.slice(0, 19).replace('T', ' ')}</div>
                </div>
                <div className="text-[#00e676] font-semibold mono">+${c.amount.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Commissions are credited to your account balance instantly when a referred trader makes a deposit. No caps, no expiry.
      </p>
    </div>
  );
};

export default Referral;
